import { Component } from '@theme/component';

/**
 * @typedef {object} TabsRefs
 * @property {HTMLButtonElement[]} tabControls - The buttons in the strip.
 * @property {HTMLElement[]} tabPanels - The descriptions, one per control.
 */

/**
 * A strip of tabs showing one description at a time.
 *
 * Controls and descriptions arrive interleaved, one pair per child block, and
 * are paired here by position rather than by id — index `n` of one list belongs
 * with index `n` of the other, which is what the markup guarantees.
 *
 * Until this runs, every description is in the document and the first tab is
 * styled as the selected one, so a page whose scripts never arrive still shows
 * all of the copy. Taking over is therefore a narrowing: `data-tabs-ready` tells
 * the stylesheet the component now owns the selected state, and everything but
 * the selected description is hidden.
 *
 * @extends {Component<TabsRefs>}
 */
class TabsComponent extends Component {
  requiredRefs = ['tabControls', 'tabPanels'];

  /** @type {number} */
  #selected = 0;

  connectedCallback() {
    super.connectedCallback();

    this.dataset.tabsReady = '';
    this.#select(0);
  }

  /**
   * Selects the tab whose control was activated.
   *
   * The control is found by its `ref` rather than by a class, so every family of
   * tabs in the theme — the benefit strip, the product info tabs — shares this
   * component without it having to know their class names. `ref` is the same
   * attribute `refs.tabControls` is collected from, so the two cannot drift.
   *
   * @param {Event} event
   */
  select(event) {
    const target = /** @type {Element | null} */ (event.target);
    const control = target?.closest('[ref="tabControls[]"]');
    const index = this.refs.tabControls.indexOf(/** @type {HTMLButtonElement} */ (control));

    if (index !== -1) this.#select(index);
  }

  /** The index currently shown. */
  get selected() {
    return this.#selected;
  }

  /**
   * @param {number} index
   */
  #select(index) {
    const { tabControls, tabPanels } = this.refs;

    this.#selected = index;

    tabControls.forEach((control, i) => {
      control.setAttribute('aria-pressed', String(i === index));
    });

    tabPanels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
